import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { supabase } from '../../lib/supabase';
import { Loader2, Upload, Edit, Trash2 } from 'lucide-react';
import {
  syncBirthdayToCalendar,
  deleteBirthdayByEmployeeId,
} from '../../lib/api/calendar-api';
import { UserAvatar } from '../../components/UserAvatar';
import { AvatarEditor } from '../../components/AvatarEditor';

/**
 * Settings Page
 * Allows employees to manage their profile information including:
 * - Display name
 * - Email (read-only)
 * - Phone number
 * - Birthday (syncs to Google Calendar)
 * - Profile picture
 */
export default function Settings() {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Form state
  const [displayName, setDisplayName] = useState(
    profile?.display_name || profile?.name || ''
  );
  const [phone, setPhone] = useState(profile?.phone || '');
  const [birthday, setBirthday] = useState(profile?.birthday || '');
  const [avatarColor, setAvatarColor] = useState(
    profile?.avatar_color || '#EF4444'
  );
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);

  // Avatar editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<string | null>(null);

  // Available avatar colors
  const avatarColors = [
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Amber', value: '#F59E0B' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Lime', value: '#84CC16' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Emerald', value: '#10B981' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Sky', value: '#0EA5E9' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Violet', value: '#8B5CF6' },
    { name: 'Purple', value: '#A855F7' },
    { name: 'Fuchsia', value: '#D946EF' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Rose', value: '#F43F5E' },
  ];

  // Sync form state with profile data when profile updates
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || profile.name || '');
      setPhone(profile.phone || '');
      setBirthday(profile.birthday || '');
      setAvatarColor(profile.avatar_color || '#EF4444');
      setAvatarUrl(profile.avatar_url || null);
    }
  }, [profile]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update profile in database
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: displayName,
          phone,
          birthday: birthday || null,
          avatar_color: avatarColor,
        })
        .eq('id', user.id);

      if (error) throw error;

      // Handle birthday changes (create, update, or delete)
      if (birthday && birthday !== profile?.birthday) {
        // Birthday was added or updated - sync to Google Calendar
        try {
          await syncBirthdayToCalendar({
            employee_id: user!.id,
            employee_name:
              displayName || profile?.name || user?.email || 'Unknown',
            birthday,
          });

          toast({
            title: 'Profile updated',
            description:
              'Your profile has been updated and birthday synced to Google Calendar.',
          });
        } catch (syncError: any) {
          console.error('Error syncing birthday to calendar:', syncError);
          toast({
            title: 'Profile updated',
            description:
              'Profile updated, but failed to sync birthday to calendar.',
            variant: 'destructive',
          });
        }
      } else if (!birthday && profile?.birthday) {
        // Birthday was cleared - delete from Google Calendar
        try {
          await deleteBirthdayByEmployeeId(user!.id);

          toast({
            title: 'Profile updated',
            description:
              'Your profile has been updated and birthday removed from Google Calendar.',
          });
        } catch (syncError: any) {
          console.error('Error deleting birthday from calendar:', syncError);
          toast({
            title: 'Profile updated',
            description:
              'Profile updated, but failed to remove birthday from calendar.',
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Profile updated',
          description: 'Your profile has been updated successfully.',
        });
      }

      // Refresh profile data to update sidebar avatar
      await refreshProfile();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 2MB.',
        variant: 'destructive',
      });
      return;
    }

    // Convert file to data URL and open editor
    const reader = new FileReader();
    reader.onload = event => {
      if (event.target?.result) {
        setImageToEdit(event.target.result as string);
        setIsEditorOpen(true);
      }
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleEditCurrentAvatar = async () => {
    if (!avatarUrl) return;

    try {
      // Extract the file path from the public URL
      // URL format: https://{project}.supabase.co/storage/v1/object/public/avatars/{userId}/{filename}
      const urlParts = avatarUrl.split('/avatars/');
      if (urlParts.length < 2) {
        throw new Error('Invalid avatar URL');
      }
      const filePath = urlParts[1];

      // Download the file as a blob using Supabase client (handles CORS)
      const { data, error } = await supabase.storage
        .from('avatars')
        .download(filePath);

      if (error) throw error;

      // Convert blob to data URL
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setImageToEdit(event.target.result as string);
          setIsEditorOpen(true);
        }
      };
      reader.readAsDataURL(data);
    } catch (error: any) {
      console.error('Error loading avatar for editing:', error);
      toast({
        title: 'Error',
        description: 'Failed to load avatar for editing.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id) return;

    setIsUploadingAvatar(true);

    try {
      // Update profile to remove avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local state immediately
      setAvatarUrl(null);

      toast({
        title: 'Avatar removed',
        description: 'Your profile picture has been removed.',
      });

      // Refresh profile data in the background
      await refreshProfile();
    } catch (error: any) {
      console.error('Error removing avatar:', error);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to remove avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleCropComplete = async (croppedImageBlob: Blob) => {
    setIsUploadingAvatar(true);

    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Upload cropped image to Supabase Storage
      const fileName = `${user.id}-${Date.now()}.jpg`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, croppedImageBlob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/jpeg',
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from('avatars').getPublicUrl(filePath);

      // Update local state immediately to show the new avatar without refresh
      setAvatarUrl(publicUrl);

      // Update profile with avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: 'Avatar updated',
        description: 'Your profile picture has been updated.',
      });

      // Refresh profile data in the background
      await refreshProfile();
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 bg-white/80 backdrop-blur-sm border-b border-slate-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-8 -mt-8">
          <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-cyan-200 via-purple-200 to-emerald-200 rounded-xl shadow-lg">
              <Edit className="w-8 h-8 text-slate-700" />
            </div>
            Settings
          </h1>
          <p className="mt-2 text-slate-600 text-lg">
            Manage your profile and preferences
          </p>
        </div>

        {/* Profile Information Card */}
        <Card className="border-2 border-slate-200 hover:border-cyan-400 transition-all duration-300 shadow-sm hover:shadow-xl bg-gradient-to-br from-white to-slate-50/30 relative overflow-hidden group">
          {/* Decorative corner */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          <CardHeader className="relative">
            <CardTitle className="text-2xl font-bold text-slate-900">
              Profile Information
            </CardTitle>
            <CardDescription className="text-slate-600">
              Update your personal information and profile picture
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-6">
              {/* Avatar Upload and Color Picker */}
              <div className="space-y-4">
                <div className="flex items-center space-x-6">
                  <div className="relative group">
                    <UserAvatar
                      avatarUrl={avatarUrl}
                      avatarColor={avatarColor}
                      name={
                        displayName || profile?.name || user?.email || undefined
                      }
                      size="xl"
                    />
                    {avatarUrl && (
                      <div className="absolute inset-0 flex items-center justify-center gap-3 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={handleEditCurrentAvatar}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                          title="Edit avatar"
                        >
                          <Edit className="h-5 w-5 text-white" />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="p-2 bg-white/20 hover:bg-red-500/80 rounded-full transition-colors"
                          title="Remove avatar"
                        >
                          <Trash2 className="h-5 w-5 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="avatar-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border-2 border-cyan-300 rounded-lg shadow-sm text-sm font-medium text-cyan-700 bg-white hover:bg-cyan-50 hover:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-100 transition-all"
                    >
                      {isUploadingAvatar ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload photo
                        </>
                      )}
                    </Label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={isUploadingAvatar}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      JPG, PNG or GIF. Max size 2MB. Click avatar to reposition.
                    </p>
                  </div>
                </div>

                {/* Avatar Color Picker */}
                <div>
                  <Label>Avatar Color</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose a color for your avatar background (shown when no
                    photo is uploaded)
                  </p>
                  <div className="grid grid-cols-9 gap-2">
                    {avatarColors.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setAvatarColor(color.value)}
                        className={`w-10 h-10 rounded-full transition-all ${
                          avatarColor === color.value
                            ? 'ring-2 ring-offset-2 ring-cyan-500 scale-110 shadow-lg'
                            : 'hover:scale-105 hover:shadow-md'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                        aria-label={`Select ${color.name} color`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Display Name */}
              <div className="relative">
                <Label
                  htmlFor="display-name"
                  className="text-sm font-medium text-slate-700"
                >
                  Display Name
                </Label>
                <Input
                  id="display-name"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  className="mt-1 h-11 border-2 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all"
                />
              </div>

              {/* Email (Read-only) */}
              <div className="relative">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-slate-700"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="mt-1 h-11 bg-slate-50 border-2 border-slate-200"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Email cannot be changed
                </p>
              </div>

              {/* Phone */}
              <div className="relative">
                <Label
                  htmlFor="phone"
                  className="text-sm font-medium text-slate-700"
                >
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1 h-11 border-2 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all"
                />
              </div>

              {/* Birthday */}
              <div className="relative">
                <Label
                  htmlFor="birthday"
                  className="text-sm font-medium text-slate-700"
                >
                  Birthday
                </Label>
                <Input
                  id="birthday"
                  type="date"
                  value={birthday}
                  onChange={e => setBirthday(e.target.value)}
                  className="mt-1 h-11 border-2 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Your birthday will be synced to the team Google Calendar
                </p>
              </div>

              {/* Save Button */}
              <div className="flex justify-end relative">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 shadow-md hover:shadow-lg transition-all"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Avatar Editor Modal */}
      {imageToEdit && (
        <AvatarEditor
          open={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false);
            setImageToEdit(null);
          }}
          imageUrl={imageToEdit}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
